// Vendor
import moment from 'moment';

// Personal
import { IBackendDate } from '@shared/interface/model.interface';
import { IDataTime } from '@shared/interface/cronjob.interface';

/**
 * Equivalent PHP de time() en secondes si milliseconds = false
 * Equivalent PHP de time() en millisecondes si milliseconds = true
 * @param milliseconds
 * @returns
 */
export function time(milliseconds = false) {
  return milliseconds
    ? new Date().getTime()
    : Math.floor(new Date().getTime() / 1000);
}

/**
 * Get seconds between now and a IBackendDate
 * @param {IBackendDate} backendDate
 * @returns {number}
 */
export function getSecondsUntilBackendDate(backendDate?: IBackendDate): number {
  if (backendDate == null) {
    return 0;
  }

  const backendDateTimestamp = new Date(backendDate.date).getTime();
  const nowTimestamp = new Date().getTime();

  return Math.ceil((backendDateTimestamp - nowTimestamp) / 1000);
}

/**
 * Format a IBackendDate to a string date with the format "jj/mm/aaaa à hh:mm:ss"
 * @param {IBackendDate} d
 * @returns {string}
 */
export function formatDateToLitteral(d: IBackendDate): string {
  return moment(d.date).format('DD/MM/YYYY à HH:mm:ss');
}

/**
 * Convert a IBackendDate to a Date
 * @param {IBackendDate} backendDate
 * @returns {Date}
 */
export function convertBackendDateToDate(backendDate: IBackendDate): Date {
  return new Date(backendDate.date);
}

/**
 * Convert a Date to a simple date (jj/mm/aaaa)
 * @param {Date} d
 * @returns {string}
 */
export function convertDateToSimpleDate(d: Date): string {
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear().toString();

  return `${day}/${month}/${year}`;
}

/**
 * Convert a Date to a full date (jj/mm/aaaa à hh:mm:ss)
 * @param d
 * @param separator
 * @param showMilliseconds
 * @returns
 */
export function convertDateToLiterral(
  d: Date,
  separator: string = ' à ',
  showMilliseconds: boolean = false,
): string {
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear().toString();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const seconds = d.getSeconds().toString().padStart(2, '0');
  const milli = showMilliseconds
    ? `.${d.getMilliseconds().toString().padStart(3, '0')}`
    : '';

  return `${day}/${month}/${year}${separator}${hours}:${minutes}:${seconds}${milli}`;
}

/**
 * Get timestamp
 * @returns
 */
export const timestamp = (
  milliseconds: boolean | undefined = false,
): number => {
  const d = new Date();
  return milliseconds ? d.getTime() : Math.floor(d.getTime() / 1000);
};

/**
 * Convert now() to a full date (jj/mm/aaaa à hh:mm:ss)
 * @returns
 */
export function getNow(separator: string = ' à '): string {
  return convertDateToLiterral(new Date(), separator);
}

export function timestampToLitteral(timestamp?: number): string | undefined {
  if (timestamp == null || timestamp === 0) {
    return undefined;
  }

  const newTimestamp =
    String(timestamp).length === 10 ? timestamp * 1000 : timestamp;

  return convertDateToLiterral(new Date(newTimestamp));
}

/**
 * Extraction time datas from seconds
 * @param providedSeconds
 * @returns
 */
export const extractTimeDataFromSeconds = (
  providedSeconds: number,
): IDataTime => {
  const nbSecondsInDay = 86400; // 60*60*24
  const nbSecondsInMonth = 2592000; // 60*60*24*30

  const months = Math.floor(providedSeconds / nbSecondsInMonth);
  const days = Math.floor(
    (providedSeconds % nbSecondsInMonth) / nbSecondsInDay,
  );
  const hours = Math.floor((providedSeconds % nbSecondsInDay) / 3600);
  const minutes = Math.floor((providedSeconds % 3600) / 60);
  const seconds = Math.floor(providedSeconds % 60);

  return {
    months,
    days,
    hours,
    minutes,
    seconds,
  };
};

/**
 *
 * @param {number} seconds
 * @param {boolean} denseMode
 * @returns
 */
export const convertSecondsInFinestTime = (
  dataTime?: IDataTime,
  shortMode: boolean = false,
): string => {
  let output = '';

  const months = dataTime?.months;
  const days = dataTime?.days;
  const hours = dataTime?.hours;
  const minutes = dataTime?.minutes;
  const seconds = dataTime?.seconds;

  if (months != null && months > 0) {
    output += `${months}${shortMode ? 'm' : ' mois'} `;
  }

  if (
    (days != null && days > 0) ||
    (months != null && months > 0 && days == null)
  ) {
    output += `${days}${shortMode ? 'j' : ' jours'} `;
  }

  output += `${(hours ?? 0).toFixed(0).padStart(2, '0')}${
    shortMode ? 'h' : ' heures'
  } `;
  output += `${(minutes ?? 0).toFixed(0).padStart(2, '0')}${
    shortMode ? 'm' : ' minutes'
  } `;
  output += `${(seconds ?? 0).toFixed(0).padStart(2, '0')}${
    shortMode ? 's' : ' secondes'
  }`;

  return output;
};
